import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Workbox } from 'workbox-window';

export function ReloadPrompt() {
    const [needRefresh, setNeedRefresh] = useState(false);
    const [offlineReady, setOfflineReady] = useState(false);
    const [wb, setWb] = useState<Workbox | null>(null);

    useEffect(() => {
        // Only verify we are in browser and PWA is enabled
        // Note: VITE_ENABLE_PWA is a string 'true' or 'false'
        const isPwaEnabled = import.meta.env.VITE_ENABLE_PWA === 'true';

        if (
            typeof window !== 'undefined' &&
            'serviceWorker' in navigator &&
            isPwaEnabled
        ) {
            const wbInstance = new Workbox('/sw.js');
            setWb(wbInstance);

            // Add event listeners
            wbInstance.addEventListener('installed', (event) => {
                if (!event.isUpdate) {
                    setOfflineReady(true);
                }
            });

            wbInstance.addEventListener('waiting', () => {
                setNeedRefresh(true);
            });

            wbInstance.addEventListener('controlling', () => {
                window.location.reload();
            });

            wbInstance.register().catch(err => {
                console.error('SW registration failed', err);
            });
        }
    }, []);

    const updateServiceWorker = () => {
        if (wb && needRefresh) {
            wb.messageSkipWaiting();
        }
    };

    const close = () => {
        setOfflineReady(false);
        setNeedRefresh(false);
    };

    return (
        <AnimatePresence>
            {(offlineReady || needRefresh) && (
                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 50 }}
                    className="fixed left-3 right-3 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-50 md:left-auto md:right-4 md:bottom-4 md:w-full md:max-w-md"
                >
                    <div className="bg-white dark:bg-zinc-950 border border-border shadow-lg rounded-lg p-4">
                        <div className="flex items-start gap-4">
                            <div className="mt-1 flex-shrink-0">
                                {offlineReady ? <Wifi className="h-5 w-5 text-green-500" /> : <RefreshCw className="h-5 w-5 text-primary animate-spin-slow" />}
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 className="text-base sm:text-lg font-semibold mb-1">
                                    {offlineReady ? 'Pronto per l\'offline' : 'Aggiornamento Disponibile'}
                                </h3>
                                <p className="text-sm text-muted-foreground mb-3">
                                    {offlineReady
                                        ? 'L\'applicazione è pronta per essere utilizzata offline.'
                                        : 'È disponibile una nuova versione. Ricarica per aggiornare.'}
                                </p>
                                <div className="flex gap-2">
                                    {needRefresh && (
                                        <Button
                                            size="sm"
                                            onClick={updateServiceWorker}
                                            className="bg-primary hover:bg-primary/90 text-white"
                                        >
                                            Aggiorna Ora
                                        </Button>
                                    )}
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={close}
                                    >
                                        Chiudi
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
